import React, { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

import { Button } from 'components/Button'
import { Container } from 'components/Container'
import { InputHookForm } from 'components/InputHookForm'
import { Spinner } from 'components/Spinner'

import { resetErrorAction } from 'root-redux/actions/common'
import {
  selectActionList,
  selectAppName,
  selectError,
} from 'root-redux/selects/common'
import { TAppDispatch } from 'root-redux/store/store'

import {
  EMAIL_VALIDATION,
  NAME_VALIDATION,
} from 'modules/contactForm/constants'
import {
  SEND_FORM,
  removeAllFilesAction,
  sendCustomFormAction,
  setIsModalShownAction,
} from 'modules/contactForm/redux'

import { APP_BG_COLORS, AppName, READABLE_APP_NAMES } from 'root-constants'

import { StyledWithdrawContactForm as S } from './WithdrawContactForm.styles'
import { WithdrawModal } from './WithdrawModal'

const WITHDRAW_TOPIC = 'Withdraw from Contract'
const HELLO_PIANO_TAGS = ['withdrawal', 'hello piano']
const DEFAULT_TAGS = ['withdrawal']

interface IWithdrawForm {
  name: string
  surname: string
  email: string
  contract: string
  userId: string
}

export const WithdrawContactForm: React.FC = () => {
  const dispatch: TAppDispatch = useDispatch()
  const appName = useSelector(selectAppName)
  const error = useSelector(selectError)
  const fetchingActionsList = useSelector(selectActionList)

  const isHelloPiano = appName === AppName.HELLO_PIANO

  const urlParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  )

  const emailFromUrl = useMemo(
    () => urlParams.get('email')?.replace(/"/g, '') ?? '',
    [urlParams],
  )

  const userIdFromUrl = useMemo(
    () => urlParams.get('user_id')?.replace(/"/g, '') ?? '',
    [urlParams],
  )

  const contract = useMemo(
    () =>
      urlParams.get('contract')?.replace(/"/g, '') ||
      `${READABLE_APP_NAMES[appName]} Premium Subscription`,
    [urlParams, appName],
  )

  const isFormSendingFromStore = useMemo(
    () => fetchingActionsList.includes(SEND_FORM),
    [fetchingActionsList],
  )

  const {
    register,
    getValues,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<IWithdrawForm>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      surname: '',
      email: isHelloPiano ? '' : emailFromUrl,
      contract,
      userId: userIdFromUrl,
    },
  })

  const onSubmit = useCallback(
    (data: IWithdrawForm) => {
      dispatch(
        sendCustomFormAction(
          {
            name: `${data.name} ${data.surname}`.trim(),
            email: data.email,
            topic: WITHDRAW_TOPIC,
            request: isHelloPiano
              ? `User ID: ${data.userId}`
              : `Contract: ${contract}`,
          },
          isHelloPiano ? HELLO_PIANO_TAGS : DEFAULT_TAGS,
          'formError',
        ),
      )
    },
    [dispatch, contract, isHelloPiano],
  )

  const handleCloseModal = useCallback(() => {
    dispatch(setIsModalShownAction(false))
    dispatch(removeAllFilesAction())

    if (window?.webkit?.messageHandlers?.jsHandler) {
      window.webkit.messageHandlers.jsHandler.postMessage('Popup was closed')
    }

    if (window?.JSBridge?.postMessage) {
      window.JSBridge.postMessage('Popup was closed')
    }

    if (!error) {
      reset()
      return
    }
    dispatch(resetErrorAction())
  }, [dispatch, error, reset])

  return (
    <S.Wrapper>
      <S.Header />
      {isFormSendingFromStore && <Spinner />}
      <Container>
        <S.Title>Withdraw from Contract</S.Title>
        <S.Form
          onSubmit={handleSubmit(onSubmit)}
          backgroundColor={APP_BG_COLORS[appName]}
        >
          <S.FormInputsWrapper>
            <InputHookForm
              register={register('name', {
                required: 'Name is required',
                validate: { ...NAME_VALIDATION },
              })}
              spellCheck={false}
              labelName="Your name *"
              isValid={!errors.name}
              validationText={errors.name?.message as string}
              marginBottom={24}
            />
            <InputHookForm
              register={register('surname', {
                required: 'Surname is required',
                validate: { ...NAME_VALIDATION },
              })}
              spellCheck={false}
              labelName="Your surname *"
              isValid={!errors.surname}
              validationText={errors.surname?.message as string}
              marginBottom={24}
            />
            <InputHookForm
              register={register(
                'email',
                isHelloPiano
                  ? {
                      required: 'Email is required',
                      validate: { ...EMAIL_VALIDATION },
                    }
                  : {},
              )}
              labelName={isHelloPiano ? 'Your email *' : 'Your email'}
              isValid={!errors.email}
              validationText={errors.email?.message as string}
              marginBottom={24}
              disabled={!isHelloPiano}
            />
            {isHelloPiano ? (
              <InputHookForm
                register={register('userId')}
                labelName="User ID"
                isValid
                marginBottom={24}
                disabled
              />
            ) : (
              <InputHookForm
                register={register('contract')}
                labelName="Contract"
                isValid
                marginBottom={24}
                disabled
              />
            )}
          </S.FormInputsWrapper>

          <Button type="submit" disabled={!isValid || isFormSendingFromStore}>
            Submit
          </Button>
        </S.Form>
      </Container>
      <WithdrawModal
        name={getValues('name')}
        surname={getValues('surname')}
        email={getValues('email')}
        userId={isHelloPiano ? getValues('userId') : undefined}
        onClose={handleCloseModal}
      />
    </S.Wrapper>
  )
}
